import { useMutation, useQueryClient } from 'react-query'

import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import {
  creditCardItemRepository,
  type RelatedItemsResult,
} from '@webapp/modules/accounts-payable/data-access/repositories/CreditCardItemRepository'
import { CREDIT_CARD_ITEM_LIST_BASE_KEY } from '@webapp/modules/accounts-payable/query-configs/creditCardItemQueries'

// Typed discriminator for the client-side precheck rejection (BHV-023) —
// the FS branches warning-modal vs generic toast on it.
export const RELATED_ITEMS_FOUND = 'RELATED_ITEMS_FOUND' as const

export class MoveRelatedItemsError extends Error {
  readonly type = RELATED_ITEMS_FOUND

  constructor(readonly results: RelatedItemsResult[]) {
    super(RELATED_ITEMS_FOUND)
  }
}

export const isMoveRelatedItemsError = (
  error: unknown
): error is MoveRelatedItemsError => error instanceof MoveRelatedItemsError

interface MoveCreditCardItemsVariables {
  targetCreditCardId: number
  itemIds: number[]
  /** Confirmed path — related items already folded into itemIds (BHV-023) */
  skipRelatedItemsCheck?: boolean
}

export const useMutateMoveCreditCardItems = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    void,
    RepoError | MoveRelatedItemsError,
    MoveCreditCardItemsVariables
  >({
    // The related-items repo read happens inside the command flow — not a
    // useQuery (no CQS violation; arch-memory client-side precheck pattern).
    mutationFn: async ({
      targetCreditCardId,
      itemIds,
      skipRelatedItemsCheck,
    }) => {
      if (!skipRelatedItemsCheck) {
        const results = await creditCardItemRepository.getRelatedItems(itemIds)

        const hasRelatedItems = results.some(
          (result) => result.relatedItemIds.length > 0
        )
        if (hasRelatedItems) {
          throw new MoveRelatedItemsError(results)
        }
      }

      await creditCardItemRepository.moveToCreditCard(
        targetCreditCardId,
        itemIds
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CREDIT_CARD_ITEM_LIST_BASE_KEY,
      })
    },
  })

  return {
    moveCreditCardItems: mutation.mutateAsync,
    isMoving: mutation.isLoading,
    error: mutation.error,
  }
}
